import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  BULK_ORDER_GRID_AXES,
  formatPowerLabel,
  getDisplayLinePowers,
  isBulkOrderSideEnabled,
  MAX_CART_LINE_QUANTITY,
  selectBulkOrderLens,
  selectGridContext,
} from '../../common/bulkOrder'
import { Icon, Link } from '../components'
import BulkOrderCountSummary from './BulkOrderCountSummary'
import { CART_PRODUCT_STICKY_HEADER_MIN_LINES } from './config'
import {
  CartLinesBody,
  CartLinesHead,
  CartLinesTable,
  CartProductActions,
  CartProductChevron,
  CartProductGroup,
  CartProductHeader,
  CartProductHeaderToggle,
  CartProductInfo,
  CartProductMeta,
  CartProductName,
  EditProductLink,
  QtyInput,
  RemoveLineButton,
  RemoveProductButton,
} from './BulkOrderCartPage.styles'

const BulkOrderCartProductGroup = ({
  group,
  defaultExpanded = false,
  disabled = false,
  readOnly = false,
  onQuantityChange,
  onRemoveLine,
  onRemoveProduct,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { cylinderSign } = useSelector(selectGridContext)
  const showSide = isBulkOrderSideEnabled()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const gridAxis = group.lines[0]?.gridAxis || BULK_ORDER_GRID_AXES.CYLINDER
  const isAdditionGrid = gridAxis === BULK_ORDER_GRID_AXES.ADDITION
  const hasStickyHeader = group.lines.length > CART_PRODUCT_STICKY_HEADER_MIN_LINES
  const groupRef = useRef(null)
  const headerRef = useRef(null)

  const updateHeaderHeight = useCallback(() => {
    if (!hasStickyHeader) return

    const groupEl = groupRef.current
    const headerEl = headerRef.current
    if (!groupEl || !headerEl) return

    groupEl.style.setProperty(
      '--cart-product-header-height',
      `${Math.ceil(headerEl.getBoundingClientRect().height)}px`,
    )
  }, [hasStickyHeader])

  useLayoutEffect(() => {
    if (!hasStickyHeader) return undefined

    updateHeaderHeight()

    const headerEl = headerRef.current
    if (!headerEl || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(updateHeaderHeight)
    observer.observe(headerEl)

    return () => observer.disconnect()
  }, [hasStickyHeader, isExpanded, updateHeaderHeight])

  const handleToggle = () => {
    setIsExpanded(prev => !prev)
  }

  const handleRemoveProduct = event => {
    event.stopPropagation()
    if (disabled || readOnly) return
    onRemoveProduct(group)
  }

  const handleEditProduct = event => {
    event.stopPropagation()
    if (readOnly) return
    dispatch(selectBulkOrderLens(group.lensId))
  }

  return (
    <CartProductGroup ref={groupRef}>
      <CartProductHeader
        ref={headerRef}
        $isExpanded={isExpanded}
        $hasStickyHeader={hasStickyHeader}
      >
        <CartProductHeaderToggle
          type="button"
          aria-expanded={isExpanded}
          onClick={handleToggle}
        >
          <CartProductChevron $isExpanded={isExpanded}>
            <Icon name="IosArrowDown" size={20} withoutTooltip />
          </CartProductChevron>
          <CartProductInfo>
            <CartProductName>{group.lensName}</CartProductName>
            <CartProductMeta>
              <BulkOrderCountSummary
                variantCount={group.lineCount}
                pieceCount={group.totalQuantity}
              />
            </CartProductMeta>
          </CartProductInfo>
        </CartProductHeaderToggle>
        {!readOnly && (
          <CartProductActions>
            <EditProductLink to={Link.BULK_ORDER} onClick={handleEditProduct}>
              <Icon name="MdCreate" size={20} />
              {t('edit')}
            </EditProductLink>
            <RemoveProductButton
              type="button"
              disabled={disabled}
              onClick={handleRemoveProduct}
            >
              <Icon name="IosTrash" size={20} />
              {t('remove product')}
            </RemoveProductButton>
          </CartProductActions>
        )}
      </CartProductHeader>

      {isExpanded && (
        <CartLinesTable $hasStickyHeader={hasStickyHeader}>
          <CartLinesHead $hasStickyHeader={hasStickyHeader} $readOnly={readOnly}>
            <tr>
              <th>{t('diameter')}</th>
              <th>{t('sphere')}</th>
              <th>{isAdditionGrid ? t('addition') : t('cylinder')}</th>
              {showSide && <th>{t('side')}</th>}
              <th>{t('quantity')}</th>
              {!readOnly && <th />}
            </tr>
          </CartLinesHead>
          <CartLinesBody $readOnly={readOnly}>
            {group.lines.map(line => {
              const display = getDisplayLinePowers(line, gridAxis, cylinderSign)
              return (
                <tr key={line.id}>
                  <td>{line.diameter}</td>
                  <td>{formatPowerLabel(display.sphere)}</td>
                  <td>
                    {formatPowerLabel(display.columnValue)}
                  </td>
                  {showSide && <td>{line.side}</td>}
                  <td>
                    {readOnly ? (
                      line.quantity
                    ) : (
                      <QtyInput
                        type="number"
                        min="1"
                        max={MAX_CART_LINE_QUANTITY}
                        step="1"
                        value={line.quantity || ''}
                        disabled={disabled}
                        onChange={event => onQuantityChange(line, event.target.value)}
                      />
                    )}
                  </td>
                  {!readOnly && (
                    <td>
                      <RemoveLineButton
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                          if (disabled) return
                          onRemoveLine(line.id)
                        }}
                      >
                        <Icon name="IosTrash" size={20} />
                      </RemoveLineButton>
                    </td>
                  )}
                </tr>
              )
            })}
          </CartLinesBody>
        </CartLinesTable>
      )}
    </CartProductGroup>
  )
}

BulkOrderCartProductGroup.propTypes = {
  group: PropTypes.shape({
    lensId: PropTypes.string.isRequired,
    lensCode: PropTypes.string.isRequired,
    lensName: PropTypes.string.isRequired,
    lineCount: PropTypes.number.isRequired,
    totalQuantity: PropTypes.number.isRequired,
    lines: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        lensId: PropTypes.string.isRequired,
        lensCode: PropTypes.string.isRequired,
        lensName: PropTypes.string.isRequired,
        diameter: PropTypes.number.isRequired,
        side: PropTypes.string.isRequired,
        sphere: PropTypes.number.isRequired,
        cylinder: PropTypes.number.isRequired,
        addition: PropTypes.number,
        gridAxis: PropTypes.oneOf(Object.values(BULK_ORDER_GRID_AXES)),
        quantity: PropTypes.number.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  defaultExpanded: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  onQuantityChange: PropTypes.func,
  onRemoveLine: PropTypes.func,
  onRemoveProduct: PropTypes.func,
}

export default BulkOrderCartProductGroup
